import React, { useState, useEffect } from "react";
import { Card, Table, Button, Space, Typography, Empty, Spin, Popconfirm, Tag } from "antd";
import { ColumnType } from "antd/es/table";
import { PlusOutlined, DeleteOutlined, AppstoreOutlined, EditOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { API_URL, getCredentials } from "../../shared/utils/Util";
import { getFetchT } from "../../shared/utils/TypedUtil";
import { useAntdTheme, useNotification } from "../../shared/hooks";
import { ItemSchemaGroup } from "./types";
import { ItemSchemaGroupForm } from "./Form";

const { Text } = Typography;

type ItemSchemaGroupEditorProps = {
  restaurantId: string;
};

export const ItemSchemaGroupEditor: React.FC<ItemSchemaGroupEditorProps> = ({ restaurantId }) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { deleteSuccess, notifyError } = useNotification();
  const [groups, setGroups] = useState<ItemSchemaGroup[] | undefined>(undefined);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [editingGroup, setEditingGroup] = useState<ItemSchemaGroup | null>(null);

  const refreshGroups = () => {
    getFetchT<{ item_schema_groups: ItemSchemaGroup[] }>(
      `${API_URL}/v1/r/${restaurantId}/admin/item_schema_groups`,
      (result) => setGroups(result.item_schema_groups || []),
      (err) => {
        notifyError(t("failed_to_fetch_item_schema_groups"), err);
        setGroups([]);
      }
    );
  };

  useEffect(() => {
    refreshGroups();
  }, [restaurantId]);

  const handleDelete = (groupId: string) => {
    setDeleting(groupId);
    fetch(`${API_URL}/v1/r/${restaurantId}/admin/item_schema_groups/${groupId}`, {
      credentials: "include",
      method: "DELETE",
      headers: {
        Authorization: `Basic ${getCredentials()}`,
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw res;
        }
        deleteSuccess(t("item_schema_group"));
        setGroups(undefined);
        refreshGroups();
      })
      .catch((err) => {
        err.text().then((errorMessage: string) => {
          notifyError(t("failed_to_delete_item_schema_group"), errorMessage);
        });
      })
      .finally(() => {
        setDeleting(null);
      });
  };

  const handleEdit = (group: ItemSchemaGroup) => {
    setEditingGroup(group);
  };

  const handleCancelEdit = () => {
    setEditingGroup(null);
  };

  const onChange = () => {
    setGroups(undefined);
    setEditingGroup(null);
    refreshGroups();
  };

  if (groups === undefined) {
    return <Spin size="large" />;
  }

  const columns: ColumnType<ItemSchemaGroup>[] = [
    {
      title: t("name"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("menu_type"),
      dataIndex: "menu_type",
      key: "menu_type",
      render: (value: string) => (
        <Tag color={value === "dine_in" ? "blue" : "green"}>
          {value === "dine_in" ? t("dine_in_pickup") : t("group_order")}
        </Tag>
      ),
      sorter: (a, b) => a.menu_type.localeCompare(b.menu_type),
      defaultSortOrder: "ascend",
    },
    {
      title: t("sequence"),
      dataIndex: "seq",
      key: "seq",
      sorter: (a, b) => a.seq - b.seq,
      defaultSortOrder: "ascend",
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_: unknown, record: ItemSchemaGroup) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            {t("edit")}
          </Button>
          <Popconfirm
            title={t("delete_item_schema_group_confirm")}
            description={t("delete_item_schema_group_description")}
            onConfirm={() => handleDelete(record.id)}
            okText={t("yes")}
            cancelText={t("no")}
          >
            <Button type="text" danger icon={<DeleteOutlined />} loading={deleting === record.id}>
              {t("delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <PlusOutlined style={{ fontSize: 20, color: token.colorSuccess }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {editingGroup ? t("edit_item_schema_group") : t("add_item_schema_group")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
          marginBottom: token.marginXL,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <ItemSchemaGroupForm
          restaurantId={restaurantId}
          onSuccess={onChange}
          editingGroup={editingGroup}
          onCancelEdit={handleCancelEdit}
        />
      </Card>

      <Card
        title={
          <Space>
            <AppstoreOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {t("current_item_schema_groups")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        {groups.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text type="secondary">{t("no_item_schema_groups_available")}</Text>}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={groups}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `${t("total")} ${total} ${t("item_schema_groups")}`,
            }}
            style={{ borderRadius: token.borderRadiusLG }}
          />
        )}
      </Card>
    </div>
  );
};
